import {
  Box,
  Typography,
  LinearProgress,
  Grid,
} from '@mui/material';
import { T, Icon, stitchPill, stitchOutlineBtn, stitchCard } from '../parts/stitch';
import { useProjectData } from '../ProjectDataContext';

function QaPanel() {
  const {
    frameioVideos,
    qaReviews,
  } = useProjectData();

  return (
    <>
        {qaReviews.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Icon name="bug_report" size={48} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
            <Typography sx={{ fontFamily: T.fontBody, color: T.outline, fontWeight: 500, mb: 1 }}>
              Geen QA reviews gevonden
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, maxWidth: 400, mx: 'auto' }}>
              Start een Video QA check om editfouten te detecteren. Reviews worden automatisch gekoppeld aan dit project.
            </Typography>
            <Box
              component="span"
              onClick={() => window.open('/video-qa', '_blank')}
              sx={{ ...stitchOutlineBtn, mt: 2, display: 'inline-flex' }}
            >
              Naar Video QA
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {qaReviews.map((review) => {
              const rResolved = review.findings_resolved || 0;
              const rTotal = review.findings_total || review.total_findings || 0;
              const rStatus = review.review_status || 'Nieuw';
              return (
                <Box
                  key={review.id}
                  onClick={() => window.open(`/video-qa?review=${review.id}`, '_blank')}
                  sx={{
                    ...stitchCard, p: 2, cursor: 'pointer',
                    border: `1px solid ${T.surfaceHigh}`,
                    '&:hover': { borderColor: T.tertiary, bgcolor: `${T.tertiaryContainer}20`, boxShadow: '0 8px 24px rgba(45,51,53,0.08)', transform: 'translateY(-2px)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Icon name="bug_report" size={24} sx={{ color: T.tertiary, opacity: 0.5, marginTop: 4 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }} noWrap>
                        {review.video_filename || `Video #${review.video_id}`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {review.preset && (
                          <Typography component="span" sx={stitchPill('#4338ca', '#e0e7ff')}>
                            {review.preset}
                          </Typography>
                        )}
                        {review.blockers > 0 && (
                          <Typography component="span" sx={stitchPill(T.error, T.errorBg)}>
                            {review.blockers} hoog
                          </Typography>
                        )}
                        {review.warnings > 0 && (
                          <Typography component="span" sx={stitchPill(T.amber, T.amberBg)}>
                            {review.warnings} medium
                          </Typography>
                        )}
                        {review.polish > 0 && (
                          <Typography component="span" sx={stitchPill(T.outline, T.surfaceLow)}>
                            {review.polish} laag
                          </Typography>
                        )}
                        <Typography component="span" sx={stitchPill(
                          rStatus === 'Verwerkt' ? '#16a34a' : rStatus === 'In behandeling' ? '#b45309' : '#64748b',
                          rStatus === 'Verwerkt' ? '#dcfce7' : rStatus === 'In behandeling' ? T.amberBg : T.surfaceLow
                        )}>
                          {rStatus}
                        </Typography>
                      </Box>
                      {rTotal > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(rResolved / rTotal) * 100}
                            sx={{
                              flex: 1, height: 4, borderRadius: 2, bgcolor: T.surfaceLow,
                              '& .MuiLinearProgress-bar': { bgcolor: rResolved >= rTotal ? T.emerald : T.tertiary, borderRadius: 2 },
                            }}
                          />
                          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                            {rResolved}/{rTotal}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, mt: 0.25, display: 'block' }}>
                        {review.created_by && review.created_by !== 'system' ? review.created_by.split('@')[0] : ''}{' '}
                        {review.created_at ? new Date(review.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Available videos for QA */}
        {(frameioVideos || []).length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontFamily: T.font, fontWeight: 600, color: T.outline, mt: 3, mb: 1 }}>
              Video's beschikbaar voor QA
            </Typography>
            <Grid container spacing={1}>
              {(frameioVideos || []).slice(0, 10).map((v) => (
                <Grid item xs={12} sm={6} key={v.id}>
                  <Box sx={{ ...stitchCard, p: 1.5, display: 'flex', gap: 1, alignItems: 'center', border: `1px solid ${T.surfaceHigh}` }}>
                    {v.thumb_small && <Box component="img" src={v.thumb_small} sx={{ width: 60, height: 40, borderRadius: '0.5rem', objectFit: 'cover' }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600 }} noWrap>{v.filename}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                        {v.duration ? `${Math.floor(v.duration / 60)}:${String(Math.floor(v.duration % 60)).padStart(2, '0')}` : ''}
                      </Typography>
                    </Box>
                    <Box
                      component="span"
                      onClick={() => window.open(`/video-qa?videoId=${v.id}`, '_blank')}
                      sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', px: 1.5, py: 0.4, borderColor: T.tertiary, color: T.tertiary }}
                    >
                      QA Check
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
    </>
  );
}

export default QaPanel;
